<!-- ProductSelectorModal.vue -->
<template>
  <div class="modal fade show" tabindex="-1" role="dialog" style="display:block;background:rgba(0,0,0,.5);">
    <div class="modal-dialog modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ $t('superUpLang.widget.modal.select_product_title') }}</h5>
          <button type="button" class="btn-close" aria-label="Close" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <div class="row g-2 align-items-end mb-3">
            <div class="col">
              <label class="form-label fw-semibold mb-1">{{ $t('superUpLang.widget.modal.provider') }}</label>
              <multiselect
                v-model="selectedProvider"
                :options="providers"
                track-by="id"
                label="name"
                :placeholder="$t('selectLabel')"
                :allow-empty="false"
                @input="onProviderChange"
              />
              <small class="text-muted" v-if="!selectedProvider">{{ $t('superUpLang.widget.modal.select_provider_first') }}</small>
            </div>
            <div class="col-auto" v-if="selectedProvider">
              <label class="form-label mb-1">{{ $t('search') }}</label>
              <input
                v-model="searchQuery"
                type="text"
                class="form-control form-control-sm"
                :placeholder="$t('search') + '…'"
                aria-label="Search products"
                style="min-width:220px"
              >
            </div>
          </div>

          <div v-if="loading" class="text-center py-4">
            <i class="bi bi-arrow-repeat spin"></i> {{ $t('loading') }}
          </div>

          <div v-else v-if="selectedProvider">
            <div class="table-responsive">
              <table class="table table-sm align-middle">
                <thead>
                <tr>
                  <th style="width:45%;">{{ $t('superUpLang.widget.modal.name') }}</th>
                  <th style="width:25%;">{{ $t('superUpLang.widget.modal.brand') }}</th>
                  <th style="width:15%;">{{ $t('superUpLang.widget.modal.price') }}</th>
                  <th style="width:15%;" class="text-end">{{ $t('superUpLang.widget.modal.action') }}</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="p in products" :key="p.id">
                  <td class="fw-semibold">{{ p.name }}</td>
                  <td>{{ p.brand || '-' }}</td>
                  <td>{{ formatPrice(p.price) }}</td>
                  <td class="text-end">
                    <button class="btn btn-primary btn-sm" @click="selectAndClose(p)">{{ $t('select') }}</button>
                  </td>
                </tr>
                <tr v-if="!products.length">
                  <td colspan="4" class="text-center text-muted py-4">{{ $t('noResults') }}</td>
                </tr>
                </tbody>
              </table>
            </div>

            <nav v-if="pagination.total > pagination.per_page" aria-label="Products pagination" class="d-flex justify-content-between align-items-center">
              <div class="small text-muted">{{ pageStart }}–{{ pageEnd }} / {{ pagination.total }}</div>
              <ul class="pagination mb-0">
                <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
                  <button class="page-link" @click="goToPage(pagination.current_page - 1)" :disabled="pagination.current_page === 1">«</button>
                </li>
                <li v-for="n in pageNumbers" :key="'p'+n" class="page-item" :class="{ active: n === pagination.current_page }">
                  <button class="page-link" @click="goToPage(n)">{{ n }}</button>
                </li>
                <li class="page-item" :class="{ disabled: pagination.current_page === pagination.last_page }">
                  <button class="page-link" @click="goToPage(pagination.current_page + 1)" :disabled="pagination.current_page === pagination.last_page">»</button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">{{ $t('cancel') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";
import HttpWidgetServices from "@/superUp-module/resources/services/httpWidgetServices";

export default {
  name: "ProductSelectorModal",
  components: { Multiselect },
  props: {
    type: { type: Number, default: 1, required: true },
    initialProviderId: { type: [String, Number], default: null }
  },
  data() {
    return {
      providers: [],
      selectedProvider: null,
      products: [],
      loading: false,
      searchQuery: "",
      searchDebounceHandle: 0,
      pagination: { current_page: 1, last_page: 1, per_page: 15, total: 0 }
    };
  },
  computed: {
    pageNumbers() {
      const total = this.pagination.last_page || 1;
      const cur = this.pagination.current_page || 1;
      const span = 7;
      let start = Math.max(1, cur - Math.floor(span / 2));
      let end = Math.min(total, start + span - 1);
      start = Math.max(1, end - span + 1);
      const arr = [];
      for (let i = start; i <= end; i++) arr.push(i);
      return arr;
    },
    pageStart() {
      const { current_page, per_page, total } = this.pagination;
      if (!total) return 0;
      return (current_page - 1) * per_page + 1;
    },
    pageEnd() {
      const { current_page, per_page, total } = this.pagination;
      return Math.min(current_page * per_page, total);
    }
  },
  watch: {
    searchQuery() {
      if (this.searchDebounceHandle) clearTimeout(this.searchDebounceHandle);
      this.searchDebounceHandle = setTimeout(() => { this.fetchProducts(1); }, 300);
    }
  },
  methods: {
    formatPrice(val) {
      if (val == null) return "-";
      return new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(+val);
    },
    async fetchProviders() {
      try {
        this.loading = true;
        const r = await HttpWidgetServices.getProviders(this.type);
        this.providers = r.data;
        if (this.initialProviderId) {
          const pre = this.providers.find(p => String(p.id) === String(this.initialProviderId));
          if (pre) {
            this.selectedProvider = pre;
            await this.fetchProducts(1);
          }
        }
      } finally { this.loading = false; }
    },
    async fetchProducts(page = 1) {
      if (!this.selectedProvider) return;
      try {
        this.loading = true;
        const r = await HttpWidgetServices.getProducts(
          {
            type: this.type,
            provider_id: this.selectedProvider.id,
            page,
            search: this.searchQuery || undefined
          });
        const data = r.data;
        this.products = data.data || [];
        const meta = data.meta || data;
        this.pagination.current_page = +meta.current_page || 1;
        this.pagination.last_page    = +meta.last_page || 1;
        this.pagination.per_page     = +meta.per_page || (this.products.length || 15);
        this.pagination.total        = +meta.total || this.products.length;
      } catch {
        this.products = [];
        this.pagination = { current_page: 1, last_page: 1, per_page: 15, total: 0 };
      } finally { this.loading = false; }
    },
    onProviderChange() { this.fetchProducts(1); },
    goToPage(n) {
      if (n < 1 || n > this.pagination.last_page || n === this.pagination.current_page) return;
      this.fetchProducts(n);
    },
    selectAndClose(product) { this.$emit('select', product); }
  },
  mounted() { this.fetchProviders(); }
};
</script>

<style scoped>
.spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
</style>
